import { Injectable } from '@angular/core';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import {
  DeviceFirmware,
  DeviceManufacturer,
  DeviceModel,
  DeviceSeries,
} from '@app/core/interfaces/allowed-device';
import { CallCenter } from '@app/core/interfaces/c5/acd/call-center';
import { Queue } from '@app/core/interfaces/c5/acd/queue';
import { Announcement } from '@app/core/interfaces/c5/announcement';
import { HolidayList } from '@app/core/interfaces/c5/holiday-list';
import { LocationGroup } from '@app/core/interfaces/c5/location-group';
import { PhoneBookContact } from '@app/core/interfaces/c5/phone-book-contact';
import { PhoneNumber } from '@app/core/interfaces/c5/phone-number';
import { RoomGroup } from '@app/core/interfaces/c5/room-group';
import { RoutingGroup } from '@app/core/interfaces/c5/routing-group';
import { TimeTable } from '@app/core/interfaces/c5/time-table';
import { EnhancedDeviceAttribute } from '@app/core/interfaces/device-attribute-type';
import { Enterprise } from '@app/core/interfaces/enterprise';
import { EnterpriseOption } from '@app/core/interfaces/enterprise-option';
import { AssignedFeatureProfile } from '@app/core/interfaces/feature-profile';
import { IvrAssistant } from '@app/core/interfaces/ivr-assistant';
import { UserListingData } from '@app/core/interfaces/portal/user-listing-data';
import { ScreenNumber } from '@app/core/interfaces/screen-number';
import { ScreenNumbersAndUsersResult } from '@app/core/interfaces/screen-numbers-and-users-result';
import { Site } from '@app/core/interfaces/site';
import { SiteWithLocations } from '@app/core/interfaces/site-with-locations';
import { TrunkCommon } from '@app/core/interfaces/trunk-common';
import { SecurityPermissionName } from '@app/core/models/permission';
import { PortalEventContext } from '@app/core/models/portal-event-context';
import { Registration } from '@app/core/models/registration';
import { UserLevel, userLevelIsEnough } from '@app/core/models/user-level';
import { AcdRepository } from '@app/core/repositories/acd.repository';
import { AnnouncementRepository } from '@app/core/repositories/announcement.repository';
import { CallCenterRepository } from '@app/core/repositories/call-center.repository';
import { DeviceAttributeRepository } from '@app/core/repositories/device-attribute.repository';
import { DeviceFirmwareRepository } from '@app/core/repositories/device-firmware.repository';
import { DeviceManufacturerRepository } from '@app/core/repositories/device-manufacturer.repository';
import { DeviceModelRepository } from '@app/core/repositories/device-model.repository';
import { DeviceSeriesRepository } from '@app/core/repositories/device-series.repository';
import { EndUserRepository } from '@app/core/repositories/end-user.repository';
import { EnterpriseRepository } from '@app/core/repositories/enterprise.repository';
import { FeatureProfileRepository } from '@app/core/repositories/feature-profile.repository';
import { HolidayListRepository } from '@app/core/repositories/holiday-list.repository';
import { IvrRepository } from '@app/core/repositories/ivr.repository';
import { LocationGroupRepository } from '@app/core/repositories/location-group.repository';
import { PhoneBookRepository } from '@app/core/repositories/phone-book-repository';
import { PhoneNumberRepository } from '@app/core/repositories/phone-number.repository';
import { RegistrationRepository } from '@app/core/repositories/registration.repository';
import { RoomGroupRepository } from '@app/core/repositories/room-group.repository';
import { RoutingGroupsRepository } from '@app/core/repositories/routing-groups.repository';
import { ScreenNumberRepository } from '@app/core/repositories/screen-number.repository';
import { SiteRepository } from '@app/core/repositories/site.repository';
import { TimeTableRepository } from '@app/core/repositories/time-table.repository';
import { TrunkRepository } from '@app/core/repositories/trunk.repository';
import {
  ApplicationEventService,
  AssignedFeatureProfilesChangedEvent,
  EndUserFeatureProfileChanged,
  EndUserLoginNameChangedEvent,
  EnterpriseOptionsChanged,
  OptionalFeatureChangedEvent,
} from '@app/core/services/application-events';
import { EnhancedAssignedFeatureProfile } from '@app/shared/models/enhanced-assigned-feature-profile';
import { EnhancedEndUser } from '@app/shared/models/enhanced-end-user';
import { EnhancedEndUserLite } from '@app/shared/models/enhanced-end-user-lite';
import { License } from '@app/shared/models/license';
import { RefreshType } from '@app/shared/models/refresh-type';
import { SimpleFeature } from '@app/shared/models/simple-feature';
import { FeatureProfileUsers, UserFeature, UserFeatures } from '@app/shared/models/user-features';
import { CachedHelperService } from '@app/shared/services/cached-helper.service';
import { cached, Cached, cachedGroup, SourceDefinition } from '@app/utils/cached';
import { toMap, uniqBy } from '@app/utils/common';
import { hasEnterpriseLicense } from '@app/utils/has-enterprise-license';
import { wrapError } from '@app/utils/rxjs';
import { isNotBlank } from '@app/utils/string';
import { Authorization } from '@business/end-user/models/authorization';
import { Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class EnterpriseContextService {
  get defaultSite$(): Observable<Site | undefined> {
    return this.enterprise.data$.pipe(map((enterprise) => enterprise.defaultSite));
  }

  private _acdQueues: Cached<Queue[]> | null = null;

  get acdQueues(): Cached<Queue[]> {
    this._acdQueues ??= this.cachedHelperService.routeAwareCached({
      dataProducer: ([enterpriseId]) => this.acdRepository.list(enterpriseId),
      id: ([enterpriseId]) => `enterprise:${enterpriseId}:acd-queues`,
      routeParameters: ['enterpriseId'],
    });

    return this._acdQueues;
  }

  private _assignedFeatureProfiles: Cached<EnhancedAssignedFeatureProfile[]> | null;

  get assignedFeatureProfiles(): Cached<EnhancedAssignedFeatureProfile[]> {
    this._assignedFeatureProfiles ??= cachedGroup(
      [this.enterprise],
      () =>
        this.featureProfileRepository
          .listAssignedFeatureProfiles()
          .pipe(map((profiles) => this.enhanceAssignedFeatureProfiles(profiles))),
      {
        id: 'enterprise-context:assigned-feature-profiles',
      }
    );

    return this._assignedFeatureProfiles;
  }

  private _callCenter: Cached<CallCenter>;

  get callCenter(): Cached<CallCenter | null> {
    this._callCenter ??= cachedGroup(
      [this.enterprise],
      ([enterprise]) =>
        hasEnterpriseLicense(enterprise.c5, License.CallCenter) ? this.callCenterRepository.get() : of(null),
      {
        id: 'enterprise-context:call-center',
        watchSourcesLoadingState: true,
      }
    );

    return this._callCenter;
  }

  private _callCenterQueues: Cached<Queue[]> | null = null;

  get callCenterQueues(): Cached<Queue[]> {
    this._callCenterQueues ??= cachedGroup(
      [this.callCenter],
      ([callCenter]) => {
        if (callCenter != null) {
          return this.callCenterRepository
            .listQueues(callCenter.id)
            .pipe(wrapError('enterprise-context-service:call-center-queues'));
        }
        return of(null);
      },
      {
        id: `enterprise-context:call-center-queues`,
      }
    );

    return this._callCenterQueues;
  }

  private _c5EndUsers: Cached<EnhancedEndUser[]> | null = null;

  get c5EndUsers(): Cached<EnhancedEndUser[]> {
    this._c5EndUsers ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () => {
        return this.enterpriseRepository.listC5EndUsers();
      },
      id: `c5-end-users`,
      routeParameters: ['enterpriseId'],
    });

    return this._c5EndUsers;
  }

  private _deviceAttributes: Cached<EnhancedDeviceAttribute[]> | null = null;

  get deviceAttributes(): Cached<EnhancedDeviceAttribute[]> {
    this._deviceAttributes ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () => {
        return this.deviceAttributeRepository.list().pipe(
          map((attributes) => {
            return attributes.map((attribute) => {
              const vm: EnhancedDeviceAttribute = {
                name: isNotBlank(attribute.displayName) ? attribute.displayName : attribute.attributeName,
                ...attribute,
              };
              return vm;
            });
          })
        );
      },
      id: ([enterpriseId]) => `enterprise:${enterpriseId}:device-attributes`,
      routeParameters: ['enterpriseId'],
    });

    return this._deviceAttributes;
  }

  private _deviceFirmwares: Cached<DeviceFirmware[]> | null = null;

  get deviceFirmwares(): Cached<DeviceFirmware[]> {
    this._deviceFirmwares ??= cached({
      clearDelay: 1000 * 60 * 60,
      dataProducer: () => this.deviceFirmwareRepository.list(),
      id: 'enterprise-context:device-firmwares',
    });

    return this._deviceFirmwares;
  }

  private _deviceManufacturers: Cached<DeviceManufacturer[]> | null = null;

  get deviceManufacturers(): Cached<DeviceManufacturer[]> {
    this._deviceManufacturers ??= cached({
      clearDelay: 1000 * 60 * 60,
      dataProducer: () => this.deviceManufacturerRepository.listForEnterprise(),
      id: 'enterprise-context:device-manufacturers',
    });

    return this._deviceManufacturers;
  }

  private _deviceModels: Cached<DeviceModel[]> | null = null;

  get deviceModels(): Cached<DeviceModel[]> {
    this._deviceModels ??= cached({
      clearDelay: 1000 * 60 * 60,
      dataProducer: () => this.deviceModelRepository.listForEnterprise(),
      id: 'enterprise-context:device-models',
    });

    return this._deviceModels;
  }

  private _deviceSeries: Cached<DeviceSeries[]> | null = null;

  get deviceSeries(): Cached<DeviceSeries[]> {
    this._deviceSeries ??= cached({
      clearDelay: 1000 * 60 * 60,
      dataProducer: () => this.deviceSeriesRepository.listForEnterprise(),
      id: 'enterprise-context:device-series',
    });

    return this._deviceSeries;
  }

  private _endUsers: Cached<EnhancedEndUserLite[]>;

  get endUsers(): Cached<EnhancedEndUserLite[]> {
    this._endUsers ??= this.cachedHelperService.routeAwareCached({
      clearDelay: 30000,
      dataProducer: () =>
        this.endUserRepository.list().pipe(wrapError('enterprise-context-service:end-users')),
      id: ([enterpriseId]) => `enterprise:${enterpriseId}:end-users`,
      routeParameters: ['enterpriseId'],
    });

    return this._endUsers;
  }

  private _enterprise: Cached<Enterprise>;

  get enterprise(): Cached<Enterprise> {
    this._enterprise ??= this.cachedHelperService.routeAwareCached({
      dataProducer: ([, enterpriseId]) => {
        return userLevelIsEnough(UserLevel.ServiceProviderAdmin, this.authenticationService.authUser)
          ? this.enterpriseRepository.getEnterpriseByNameOverRedirPath(enterpriseId)
          : this.enterpriseRepository.getEnterprise(enterpriseId);
      },
      id: ([serviceProviderId, enterpriseId]) =>
        `service-provider:${serviceProviderId}:enterprise-${enterpriseId}:`,
      rememberTtl: 10_000,
      routeParameters: ['serviceProviderId', 'enterpriseId'],
    });

    return this._enterprise;
  }

  private _enterpriseOptions: Cached<EnterpriseOption[]>;

  get enterpriseOptions(): Cached<EnterpriseOption[]> {
    if (this._enterpriseOptions == null) {
      const [cached, connector] = this.cachedHelperService.routeAwareConnectedCached({
        context: PortalEventContext.ENTERPRISE_OPTIONS,
        clearDelay: 30_000,
        dataProducer: () => this.enterpriseRepository.getEnterpriseOptions(),
        id: ([enterpriseId]) => `enterprise:${enterpriseId}:enterprise-options`,
        reloadInterval: 600_000, // 10 minutes, if they change we should be notified from back-end with an event
        routeParameters: ['enterpriseId'],
      });

      this._enterpriseOptions = cached;

      connector.subscribe({
        next: (value) => {
          const eos = value.data as EnterpriseOption[];
          this._enterpriseOptions.data$.pipe(take(1)).subscribe((old) => {
            this._enterpriseOptions.setData(eos);
            this.applicationEventService.emit(EnterpriseOptionsChanged({ old, new: eos }));
          });
        },
      });
    }

    return this._enterpriseOptions;
  }

  /**
   * Cached source that emits only values if the enterprise options actually changed.
   * Cached groups that use this source will not switch into a loading state while this source is loading.
   */
  get enterpriseOptionsSource(): SourceDefinition<EnterpriseOption[]> {
    return {
      cached: this.enterpriseOptions,
      ignoreLoadingState: true,
      skipWhen: (a, b) => {
        return this.enterpriseOptionsDiffer(a, b) === false;
      },
    };
  }

  private _holidayLists: Cached<HolidayList[]> | null = null;

  get holidayLists(): Cached<HolidayList[]> {
    this._holidayLists ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () => {
        return this.holidayListRepository.list();
      },
      id: ([enterpriseId]) => `enterprise:${enterpriseId}:holiday-lists`,
      routeParameters: ['enterpriseId'],
    });

    return this._holidayLists;
  }

  private _ivrAssistants: Cached<IvrAssistant[]> | null = null;

  get ivrAssistants(): Cached<IvrAssistant[]> {
    this._ivrAssistants ??= this.cachedHelperService.routeAwareCached({
      dataProducer: ([enterpriseId]) => {
        if (
          this.authenticationService.hasAnyPermission(
            SecurityPermissionName.VIEW_IVR,
            SecurityPermissionName.VIEW_ANY_IVR
          ) &&
          this.authenticationService.userMinimalLevel(UserLevel.EnterpriseAdmin)
        ) {
          return this.ivrRepository.list(enterpriseId);
        } else {
          return of([]);
        }
      },
      id: ([enterpriseId]) => `enterprise:${enterpriseId}:ivr-assistants`,
      routeParameters: ['enterpriseId'],
    });

    return this._ivrAssistants;
  }

  private _parentHolidayLists: Cached<HolidayList[]> | null = null;

  get parentHolidayLists(): Cached<HolidayList[]> {
    this._parentHolidayLists ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () => {
        return this.holidayListRepository.listParentHolidayLists();
      },
      id: ([enterpriseId]) => `enterprise:${enterpriseId}:parent-holiday-lists`,
      routeParameters: ['enterpriseId'],
    });

    return this._parentHolidayLists;
  }

  private _phoneBook: Cached<PhoneBookContact[]> | null = null;

  get phoneBook(): Cached<PhoneBookContact[]> {
    this._phoneBook ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () =>
        this.phoneBookRepository.list().pipe(wrapError('enterprise-context-service:phone-book')),
      id: ([enterpriseId]) => `enterprise:${enterpriseId}:phone-book`,
      routeParameters: ['enterpriseId'],
    });

    return this._phoneBook;
  }

  private _phoneNumbers: Cached<PhoneNumber[]> | null = null;

  get phoneNumbers(): Cached<PhoneNumber[]> {
    this._phoneNumbers ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () => {
        return this.phoneNumberRepository.list();
      },
      id: ([enterpriseId]) => `enterprise:${enterpriseId}:phone-numbers`,
      routeParameters: ['enterpriseId'],
    });

    return this._phoneNumbers;
  }

  private _registrations: Cached<Registration[]> | null = null;

  get registrations(): Cached<Registration[]> {
    this._registrations ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () => this.registrationRepository.list(),
      id: ([enterpriseId]) => `enterprise:${enterpriseId}:registrations`,
      routeParameters: ['enterpriseId'],
    });

    return this._registrations;
  }

  private _rooms: Cached<RoomGroup[]> | null = null;

  get rooms(): Cached<RoomGroup[]> {
    this._rooms ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () => {
        return this.authenticationService.ifAnyPermission$(
          [SecurityPermissionName.VIEW_ROOMS],
          this.roomGroupRepository.listEnterpriseRooms().pipe(wrapError('enterprise-context-service:rooms')),
          []
        );
      },
      id: ([enterpriseId]) => `enterprise:${enterpriseId}:rooms`,
      routeParameters: ['enterpriseId'],
    });

    return this._rooms;
  }

  private _routingGroups: Cached<RoutingGroup[]> | null = null;

  get routingGroups(): Cached<RoutingGroup[]> {
    this._routingGroups ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () =>
        this.routingGroupsRepository.list().pipe(wrapError('enterprise-context-service:routing-groups')),
      id: ([enterpriseId]) => `enterprise:${enterpriseId}:routing-groups`,
      routeParameters: ['enterpriseId'],
    });

    return this._routingGroups;
  }

  private _screenNumbers: Cached<ScreenNumber[]> | null = null;

  get screenNumbers(): Cached<ScreenNumber[]> {
    this._screenNumbers ??= this.cachedHelperService.routeAwareCached({
      clearDelay: 30_000,
      dataProducer: () =>
        this.screenNumberRepository
          .listScreenNumbers()
          .pipe(wrapError('enterprise-context-service:screen-numbers')),
      id: ([enterpriseId]) => `enterprise:${enterpriseId}:screen-numbers`,
      routeParameters: ['enterpriseId'],
    });

    return this._screenNumbers;
  }

  private _screenNumbersAndUsers: Cached<ScreenNumbersAndUsersResult> | null = null;

  get screenNumbersAndUsers(): Cached<ScreenNumbersAndUsersResult> {
    this._screenNumbersAndUsers ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () =>
        this.screenNumberRepository
          .listScreenNumbersAndUsers()
          .pipe(wrapError('enterprise-context-service:screen-numbers-and-users')),
      id: ([enterpriseId]) => `enterprise:${enterpriseId}:screen-numbers-and-users`,
      routeParameters: ['enterpriseId'],
    });

    return this._screenNumbersAndUsers;
  }

  private _sites: Cached<{ sites: Site[]; siteMap: Record<number, Site> }> | null = null;

  get sites(): Cached<{ sites: Site[]; siteMap: Record<number, Site> }> {
    this._sites ??= this.cachedHelperService.routeAwareCached({
      clearDelay: 30000,
      dataProducer: () =>
        this.siteRepository.listSites().pipe(
          map((sites) => {
            return {
              sites,
              siteMap: toMap(sites, 'id'),
            };
          }),
          wrapError('enterprise-context-service:sites')
        ),
      id: ([enterpriseId]) => `enterprise:${enterpriseId}:sites`,
      routeParameters: ['enterpriseId'],
    });

    return this._sites;
  }

  private _sitesWithLocations: Cached<SiteWithLocations[]>;

  get sitesWithLocations(): Cached<SiteWithLocations[]> {
    this._sitesWithLocations ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () =>
        this.siteRepository
          .listSitesWithLocations()
          .pipe(wrapError('enterprise-context-service:sites-with-locations')),
      id: ([enterpriseId]) => `enterprise:${enterpriseId}:sites-with-locations`,
      routeParameters: ['enterpriseId'],
    });

    return this._sitesWithLocations;
  }

  private _timeTables: Cached<TimeTable[]> | null = null;

  get timeTables(): Cached<TimeTable[]> {
    this._timeTables ??= this.cachedHelperService.routeAwareCached({
      dataProducer: ([enterpriseId, userId]) => {
        return this.timeTableRepository.list(enterpriseId, userId);
      },
      id: ([enterpriseId]) => `enterprise:${enterpriseId}:time-tables`,
      routeParameters: ['enterpriseId'],
    });

    return this._timeTables;
  }

  private _trunks: Cached<TrunkCommon[]> | null = null;

  get trunks(): Cached<TrunkCommon[]> {
    this._trunks ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () => this.trunkRepository.list().pipe(wrapError('enterprise-context-service:trunks')),
      id: ([enterpriseId]) => `enterprise:${enterpriseId}:trunks`,
      routeParameters: ['enterpriseId'],
    });

    return this._trunks;
  }

  private _userFeatures: Cached<UserFeatures> | null = null;

  get userFeatures(): Cached<UserFeatures> {
    this._userFeatures ??= cachedGroup(
      [this.endUsers],
      ([endUsers]) => {
        return this.featureProfileRepository.listFeatureProfilesPerUser().pipe(
          map((featureProfilesWithUsers) => {
            const userFeaturesMap: Record<number, UserFeature[]> = {};
            const profileMap = toMap(featureProfilesWithUsers, 'name');

            endUsers.forEach((user) => {
              const features: UserFeature[] = [];
              const profile = profileMap[user.featureProfileName];

              if (profile != null) {
                profile.features.forEach((feature) => {
                  features.push({
                    id: feature.id,
                    name: feature.name,
                    optional: false,
                  });
                });

                profile.optionalFeatures.forEach((feature) => {
                  if (feature.userIds?.includes(user.id)) {
                    features.push({
                      id: feature.id,
                      name: feature.name,
                      optional: true,
                    });
                  }
                });
              }

              userFeaturesMap[user.id] = features;
            });

            const allFeatures = featureProfilesWithUsers
              .flatMap((profile) => profile.features.concat(profile.optionalFeatures))
              .map(({ id, name }) => {
                return {
                  id,
                  name,
                };
              });

            const usersByFeatureProfile: Record<string, FeatureProfileUsers> = {};

            featureProfilesWithUsers.forEach(({ id, name, users }) => {
              usersByFeatureProfile[name] = {
                id,
                name,
                users: users.map((user) => {
                  const matchingEndUser = endUsers.find((endUser) => endUser.id === user.id) || user;

                  return {
                    id: matchingEndUser.id,
                    loginName: matchingEndUser.loginName,
                    firstName: matchingEndUser.firstName,
                    lastName: matchingEndUser.lastName,
                    auth: matchingEndUser.auth,
                    role: matchingEndUser.role,
                    siteId: matchingEndUser.siteId,
                  };
                }),
              };
            });

            const virtualUsers = endUsers.filter((endUser) => endUser.auth === Authorization.FORBID_DEVICE);

            return {
              allFeatures: uniqBy(allFeatures, (feature) => feature.name),
              userMap: userFeaturesMap,
              usersByFeatureProfile,
              virtualUsers,
            };
          })
        );
      },
      {
        id: `enterprise-context:user-features`,
      }
    );

    return this._userFeatures;
  }

  private _users: Cached<UserListingData[]>;

  get users(): Cached<UserListingData[]> {
    this._users ??= this.cachedHelperService.routeAwareCached({
      clearDelay: 30000,
      dataProducer: () =>
        this.endUserRepository.listUsers().pipe(wrapError('enterprise-context-service:end-users')),
      id: ([enterpriseId]) => `enterprise:${enterpriseId}:end-users`,
      routeParameters: ['enterpriseId'],
    });

    return this._users;
  }

  private _featureMap: Cached<Record<string, FeatureMapEntry>> | null = null;

  get featureMap() {
    this._featureMap ??= cachedGroup(
      [this.users, this.userFeatures],
      ([users, { allFeatures, userMap }]) => {
        const featureMap: Record<string, FeatureMapEntry> = {};

        allFeatures.forEach((feature) => {
          const usersWithFeature = Object.entries(userMap)
            .filter(([, features]) => features.some((userFeature) => userFeature.name === feature.name))
            .map(([userId]) => users.find((user) => user.id === Number(userId)));

          featureMap[feature.name] = {
            feature,
            users: usersWithFeature,
          };
        });

        return of(featureMap);
      },
      {
        id: `enterprise-context:feature-map`,
      }
    );

    return this._featureMap;
  }

  constructor(
    private acdRepository: AcdRepository,
    private announcementRepository: AnnouncementRepository,
    private applicationEventService: ApplicationEventService,
    private authenticationService: AuthenticationService,
    private cachedHelperService: CachedHelperService,
    private callCenterRepository: CallCenterRepository,
    private enterpriseRepository: EnterpriseRepository,
    private endUserRepository: EndUserRepository,
    private featureProfileRepository: FeatureProfileRepository,
    private holidayListRepository: HolidayListRepository,
    private ivrRepository: IvrRepository,
    private locationGroupRepository: LocationGroupRepository,
    private phoneBookRepository: PhoneBookRepository,
    private phoneNumberRepository: PhoneNumberRepository,
    private registrationRepository: RegistrationRepository,
    private roomGroupRepository: RoomGroupRepository,
    private routingGroupsRepository: RoutingGroupsRepository,
    private screenNumberRepository: ScreenNumberRepository,
    private siteRepository: SiteRepository,
    private timeTableRepository: TimeTableRepository,
    private trunkRepository: TrunkRepository,
    private deviceManufacturerRepository: DeviceManufacturerRepository,
    private deviceSeriesRepository: DeviceSeriesRepository,
    private deviceModelRepository: DeviceModelRepository,
    private deviceFirmwareRepository: DeviceFirmwareRepository,
    private readonly deviceAttributeRepository: DeviceAttributeRepository
  ) {
    this.applicationEventService.on$(EndUserLoginNameChangedEvent).subscribe(() => {
      this._endUsers?.reload(false);
    });

    this.applicationEventService.on$(AssignedFeatureProfilesChangedEvent).subscribe(() => {
      this._assignedFeatureProfiles?.reload(false);
    });

    this.applicationEventService.on$(OptionalFeatureChangedEvent).subscribe(() => {
      this._enterpriseOptions.reload(false);
      this._userFeatures?.reload();
    });

    this.applicationEventService.on$(EndUserFeatureProfileChanged).subscribe(() => {
      this._endUsers.reload();
      this._userFeatures.reload();
    });
  }

  private _announcementsForSite: Cached<Announcement[]> | null = null;

  announcementsForSite(siteId: number): Cached<Announcement[]> {
    const dataProducer = () =>
      this.announcementRepository
        .listForSite(siteId)
        .pipe(wrapError('enterprise-context-service:announcements-for-site'));
    const id: string = `site:${siteId}:announcements`;

    if (this._announcementsForSite == null) {
      this._announcementsForSite = cached({
        dataProducer,
        id,
      });
    } else {
      this._announcementsForSite.changeIdentity(id, dataProducer);
    }

    return this._announcementsForSite;
  }

  endUsers$(): Observable<EnhancedEndUserLite[]> {
    return this.endUsers.data$;
  }

  private _locationGroupsForSite: Cached<LocationGroup[]> | null = null;

  locationGroupsForSite(siteId: number): Cached<LocationGroup[]> {
    const dataProducer = () => this.getLocationGroupsForSite(siteId);
    const id: string = `site:${siteId}:location-groups`;

    if (this._locationGroupsForSite == null) {
      this._locationGroupsForSite = cached({
        dataProducer,
        id,
      });
    } else {
      this._locationGroupsForSite.changeIdentity(id, dataProducer);
    }

    return this._locationGroupsForSite;
  }

  refresh(type: RefreshType): void {
    if (type === RefreshType.EndUsers) {
      this._endUsers?.reloadOrClear();
    } else if (type === RefreshType.ScreenNumbers) {
      this._screenNumbers?.reloadOrClear();
    }
  }

  reloadEnterpriseOptions(): Observable<void> {
    return this.enterpriseOptions.reload();
  }

  private _routingGroupsForSite: Cached<RoutingGroup[]> | null = null;

  routingGroupsForSite(siteId: number): Cached<RoutingGroup[]> {
    const dataProducer = () => this.getRoutingGroupsForSite(siteId);
    const id: string = `site:${siteId}:routing-groups`;

    if (this._routingGroupsForSite == null) {
      this._routingGroupsForSite = cached({
        dataProducer,
        id,
      });
    } else {
      this._routingGroupsForSite.changeIdentity(id, dataProducer);
    }

    return this._routingGroupsForSite;
  }

  private _timeTablesForSite: Cached<TimeTable[]> | null = null;

  timeTablesForSite(siteId: number): Cached<TimeTable[]> {
    const dataProducer = () =>
      this.timeTableRepository
        .listForSite(siteId)
        .pipe(wrapError('enterprise-context-service:data-producer'));
    const id: string = `site:${siteId}:routing-groups`;

    if (this._timeTablesForSite == null) {
      this._timeTablesForSite = cached({
        dataProducer,
        id,
      });
    } else {
      this._timeTablesForSite.changeIdentity(id, dataProducer);
    }

    return this._timeTablesForSite;
  }

  /**
   * Returns true if the given list of enterprise options are the same.
   */
  private enterpriseOptionsDiffer(a: EnterpriseOption[], b: EnterpriseOption[]): boolean {
    const toString = (option: EnterpriseOption) =>
      `${option.name}:${option.available}:${option.count}:${option.used}:${option.active}`;
    const sortedA = a.map(toString).sort();
    const sortedB = b.map(toString).sort();

    return sortedA.join(',') !== sortedB.join(',');
  }

  private enhanceAssignedFeatureProfiles(
    profiles: AssignedFeatureProfile[]
  ): EnhancedAssignedFeatureProfile[] {
    return profiles.map((profile) => {
      // unlimited in usage if it is a "default" profile or the `count` (max usage) is not set
      const isUsageUnlimited = profile.definition ? profile.definition.default : profile.count == null;
      const isDisabled = isUsageUnlimited ? false : (profile.used ?? 0) >= (profile.count ?? 0);

      return {
        ...profile,
        disabled: isDisabled,
      };
    });
  }

  private getLocationGroupsForSite(siteId: number): Observable<LocationGroup[]> {
    return this.locationGroupRepository
      .listForSite(siteId)
      .pipe(wrapError('enterprise-context-service:location-groups'));
  }

  private getRoutingGroupsForSite(siteId: number): Observable<RoutingGroup[]> {
    return this.routingGroupsRepository
      .listForSite(siteId)
      .pipe(wrapError('enterprise-context-service:routing-groups'));
  }
}

export interface FeatureMapEntry {
  feature: SimpleFeature;
  users: UserListingData[];
}
